/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the drugShipment management page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { RequestState, IRequestState } from '@ngt/request-utilities';

import { Button, DialogTitle, DialogContent, Dialog, DialogActions, Grid, MenuItem } from '@material-ui/core';

import CircularProgress from '@material-ui/core/CircularProgress';
import * as Yup from 'yup';

import { Field, FormRenderProps } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { useSnackbar } from 'notistack';
import { Action } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../../api/Neon.Dtos';
import { AsyncForm } from '../../../components/FinalForm/AsyncForm';
import { SelectWrapper } from '../../../components/FinalForm/FinalFormControls/SelectWrapper';
import { PatientDrugsActions } from '../../../store/modules/drugmanagement/patientdrugs';
import useAsyncFunction from '../../../hooks/useAsyncFunction';


/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IPatientDrugsModalProps {
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    patientDrugs: Dtos.PatientDrug[] | null;
    patientDrugsLoadState: IRequestState<Dtos.ResponseStatus>;
    drugs: Dtos.Drug[] | null;
    drugLoadState: IRequestState<Dtos.ResponseStatus>;
    onCloseClick?: () => void;
    onFormSave?: () => void;
}

interface IPatientDrugsFormProps extends FormRenderProps<IPatientDrugForm> {
    patientDrugs: Dtos.PatientDrug[] | null;
    patientDrugsLoadState: IRequestState<Dtos.ResponseStatus>;
    drugs: Dtos.Drug[] | null;
    drugLoadState: IRequestState<Dtos.ResponseStatus>;
    setModalOpen: (open: boolean) => void;
    onFormSave?: () => void;
}

interface IPatientDrugForm {
    patientDrugs: Dtos.PatientDrug[] | null;
}

const formSubscriptionParams = {
    submitting: true,
    pristine: false,
    submitError: true,
    dirtySinceLastSubmit: true,
    hasValidationErrors: true,
    valid: true,
    validating: true,
    errors: true,
    form: true
};

const validation = Yup.object().shape<IPatientDrugForm>({
    patientDrugs: Yup.array()
        .min(1, 'At least drug status must exist on this form')
        .of(Yup.object<Dtos.PatientDrug>().shape({
            drugId: Yup.number().required(),
            patientTreatmentStatus: Yup.number().required()
        }))
});

const setPayload = (action: Action, payload: any[]) => {
    if (action.type === PatientDrugsActions.save.type) {
        const drugCheck = payload as unknown as IPatientDrugForm;

        if (drugCheck &&
            drugCheck.patientDrugs) {
            return { type: action.type, payload: drugCheck.patientDrugs };
        }
    }

    if (!(payload instanceof Array)) {
        return { type: action.type, payload };
    }

    if (payload.length === 1) {
        return { type: action.type, payload: payload[0] };
    }

    return {
        type: action.type,
        payload,
        args: true,
    };
};

const PatientDrugsForm: React.FunctionComponent<IPatientDrugsFormProps> = ({
    patientDrugs,
    patientDrugsLoadState,
    drugs,
    drugLoadState,
    setModalOpen,
    onFormSave,
    ...formRenderProps
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const onOkClick = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {

        var formPatientDrugs = formRenderProps.form.getFieldState('patientDrugs')?.value ?? [];

        if (formPatientDrugs.filter(x => x.patientTreatmentStatus === Dtos.PatientTreatmentStatus.OnTreatment)?.length > 1) {
            enqueueSnackbar(`Patient cannot be on treatment for both drugs.`, { variant: 'error' });
            return;
        }

        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {
                setModalOpen(false);

                if (onFormSave) {
                    onFormSave();

                    enqueueSnackbar(`Patient treatment status successfully saved.`, { variant: 'success' });
                }
            }
        }
        catch (error: any) {
            const errorMessage = error.message ?
                error.message :
                'Unknown error';

            enqueueSnackbar(`An error occurred while attempting to save the patient drugs status: ${errorMessage}`, { variant: 'error' });
        }
    }, [setModalOpen, onFormSave, formRenderProps, enqueueSnackbar]);

    const onCloseClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setModalOpen(false);
    }, [setModalOpen]);

    const formDisabled = React.useMemo(() => {
        return formRenderProps.submitting;
    }, [formRenderProps.submitting]);

    const patientTreatmentStatuses = React.useMemo(() => {
        return [
            <MenuItem key={Dtos.PatientTreatmentStatus.OnTreatment} value={Dtos.PatientTreatmentStatus.OnTreatment}>On Treatment</MenuItem>,
            <MenuItem key={Dtos.PatientTreatmentStatus.OffTreatment} value={Dtos.PatientTreatmentStatus.OffTreatment}>Off Treatment</MenuItem>
        ];
    }, []);

    const patientDrugItems = React.useMemo(() => {
        if (patientDrugs &&
            patientDrugs.length > 0) {
            return <FieldArray name="patientDrugs">
                {({ fields }) => {
                    return fields.map((field, index) => {
                        const drugId = patientDrugs[index].drugId;

                        return (
                            <React.Fragment key={index}>
                                <Grid item xs={6}>
                                    {
                                        drugLoadState && drugLoadState.state && drugLoadState.state === RequestState.Pending ?
                                            <CircularProgress /> :
                                            drugs?.find(d => d.id === drugId)?.drugName
                                    }
                                </Grid>
                                <Grid item xs={6}>
                                    <Field
                                        name={`${field}.patientTreatmentStatus`}
                                        component={SelectWrapper}
                                        type="text"
                                        label="Status"
                                        disabled={formDisabled}
                                        formControlProps={{
                                            fullWidth: true
                                        }}
                                    >
                                        {
                                            patientTreatmentStatuses
                                        }
                                    </Field>
                                </Grid>
                            </React.Fragment>
                        )
                    });
                }
            }
            </FieldArray>;
        }

        return null;
    }, [patientDrugs, drugs, drugLoadState, patientTreatmentStatuses, formDisabled]);

    return (
        <>
            <DialogContent>
                <Grid container alignItems="center" spacing={2}>
                    {
                        patientDrugItems
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseClick} color="primary" disabled={formDisabled}>
                    Cancel
                </Button>
                <Button onClick={onOkClick}
                    color="primary" autoFocus disabled={formDisabled}>
                    OK
                </Button>
            </DialogActions>
        </>
    );
};

const PatientDrugsModal: React.FunctionComponent<IPatientDrugsModalProps> = ({
    patientDrugs,
    patientDrugsLoadState,
    drugs,
    drugLoadState,
    modalOpen,
    setModalOpen,
    onCloseClick,
    onFormSave
}) => {

    const patientDrugData = React.useMemo(() => {
        const patientDrugDataForm: IPatientDrugForm = Object.assign({}, { patientDrugs });
        return patientDrugDataForm;
    }, [patientDrugs]);

    const patientDrugsForm = React.useCallback((props: FormRenderProps<IPatientDrugForm>) => {
        return (
            <PatientDrugsForm
                patientDrugs={patientDrugs}
                patientDrugsLoadState={patientDrugsLoadState}
                drugs={drugs}
                drugLoadState={drugLoadState}
                setModalOpen={setModalOpen}
                onFormSave={onFormSave}
                {...props}
            />
        );
    }, [patientDrugs, patientDrugsLoadState, setModalOpen, onFormSave, drugLoadState, drugs]);

    const asyncSave = useAsyncFunction(PatientDrugsActions.save, PatientDrugsActions.saveSuccess, PatientDrugsActions.saveFailure, setPayload);

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth={true}
                maxWidth="sm"
            >
                <DialogTitle id="alert-dialog-title">
                    Save Patient Drugs Status
                </DialogTitle>
                <AsyncForm
                    initialValues={patientDrugData}
                    validate={validation}
                    onSubmit={asyncSave}
                    formSubscription={formSubscriptionParams}
                    validationDebounceTimeout={200}
                    component={patientDrugsForm}
                    mutators={{
                        ...arrayMutators
                    }}
                />
            </Dialog>
        </>
    );
}

export default PatientDrugsModal;